import { useState, useEffect, FormEvent } from 'react'
import { supabase } from '../lib/supabase'

interface PasswordRequirement {
  label: string
  test: (password: string) => boolean
}

const requirements: PasswordRequirement[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'Contains a lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'Contains an uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'Contains a number', test: (p) => /\d/.test(p) },
]

function ResetPassword() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [checkingAuth, setCheckingAuth] = useState(true)

  useEffect(() => {
    const checkSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      setIsAuthenticated(!!session)
      setCheckingAuth(false)
    }
    checkSession()
  }, [])

  const isPasswordValid = requirements.every((req) => req.test(password))
  const passwordsMatch = password === confirmPassword && password.length > 0

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!isPasswordValid) {
      setError('Please meet all password requirements')
      return
    }

    if (!passwordsMatch) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    const { error: updateError } = await supabase.auth.updateUser({
      password: password,
    })

    setLoading(false)

    if (updateError) {
      setError(updateError.message)
      return
    }

    setSuccess(true)
    // Sign out after password reset for security
    await supabase.auth.signOut()
  }

  if (checkingAuth) {
    return (
      <div className="card">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="card">
        <div className="error-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <div className="card-header">
          <h1>Session Expired</h1>
          <p>Your password reset link has expired or is invalid.</p>
        </div>
        <div className="alert alert-error">
          Please request a new password reset link from the app.
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="card">
        <div className="success-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <div className="card-header">
          <h1>Password Updated</h1>
          <p>Your password has been successfully changed.</p>
        </div>
        <div className="alert alert-success">
          You can now sign in to the app with your new password.
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="card-header">
        <h1>Reset Password</h1>
        <p>Enter your new password below</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="password">New Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter new password"
            autoComplete="new-password"
            required
          />
          <div className="password-requirements">
            <p>Password must have:</p>
            <ul>
              {requirements.map((req, index) => (
                <li key={index} className={req.test(password) ? 'valid' : ''}>
                  {req.label}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            autoComplete="new-password"
            required
          />
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || !isPasswordValid || !passwordsMatch}
        >
          {loading && <span className="spinner"></span>}
          {loading ? 'Updating...' : 'Update Password'}
        </button>
      </form>
    </div>
  )
}

export default ResetPassword
